import { getStore } from "../../store/store";
import { AssistantItem } from "../../store/store.types";
import { removeHtmlTagsAndEntities } from "../../utils/removeHtmlTagsAndEntities";
import { ROLES } from "../../utils/constants";
import { UserFromAPI } from "../../types/auth.types";
import { ClientData, KioskCallbackTask, SessionInfo } from "./types";

export const KIOSK_CALLBACK_TASK_KEY_PREFIX = "kiosk_callback_";

export const formatClientName = (clientData: ClientData | undefined) => {
    const fullName = clientData?.user_id
        ? `${clientData.user_id.first_name} ${clientData.user_id.last_name}`
        : "";
    const clientId = clientData?.client_id || "";
    const clientName = clientId ? `${clientId} - ${fullName}` : fullName || "Cliente";
    return clientName;
};

export const isClinicReceptionist = (
    clientData: ClientData | undefined,
    currentUser: UserFromAPI | undefined,
    userTodayClinic: number | Array<number> | undefined
) => {
    const sessionClinicId = clientData?.clinic_id?.id;
    const isClinicReceptionAccount = !!currentUser && clientData?.clinic_id?.receptionist === currentUser.id;
    const isReceptionistRole = currentUser?.role?.id === ROLES.recepcionist;
    const userTodayClinicIds = [userTodayClinic].flat();
    const isSameClinic = !!sessionClinicId && userTodayClinicIds.includes(sessionClinicId);
    const isReceptionist = isClinicReceptionAccount || (isReceptionistRole && isSameClinic);
    return isReceptionist;
};

export const isInternalStaff = (clientData: ClientData | undefined) => {
    const clientId = clientData?.client_id || "";
    const isInternal = clientId.startsWith("I");
    return isInternal;
};

export const addTaskToAssistant = (task: any, sessionInfo: SessionInfo) => {
    if (task.hidden_field_automations?.startsWith(KIOSK_CALLBACK_TASK_KEY_PREFIX)) return;

    const rawContent = task.content || "Tarefa sem descrição";
    const cleanContent = removeHtmlTagsAndEntities(rawContent);
    const truncatedContent = cleanContent.length > 300 ? cleanContent.substring(0, 300) + "..." : cleanContent;

    const assistantItem: AssistantItem = {
        id: `task-${task.id}-session-${sessionInfo.sessionId}`,
        type: "task",
        sessionId: sessionInfo.sessionId,
        treatmentId: sessionInfo.treatmentId,
        clientName: sessionInfo.clientName,
        content: truncatedContent,
        linkPath: `/tasks/${task.id}/detail`,
        createdAt: new Date().toISOString(),
    };

    getStore().assistantDialog.addItem(assistantItem);
};

const extractKioskCallbackPhone = (callbackTaskKey: string | null) => {
    const phone = callbackTaskKey?.split("_").pop() ?? "";
    return phone;
};

const readContentParagraphs = (content: string | null) => {
    const contentDocument = new DOMParser().parseFromString(content ?? "", "text/html");
    const paragraphs = Array.from(contentDocument.querySelectorAll("p")).map(
        (paragraph) => paragraph.textContent?.trim() ?? ""
    );
    return paragraphs;
};

const buildCallerLinkPath = (task: KioskCallbackTask) => {
    const relatedCaller = task.related_with?.[0];
    const relatedItem = relatedCaller?.item;
    const relatedItemId = typeof relatedItem === "object" && relatedItem !== null ? relatedItem.id : relatedItem;
    const callerLinkPath =
        relatedCaller?.collection && relatedItemId ? `/${relatedCaller.collection}/${relatedItemId}/detail` : undefined;
    return callerLinkPath;
};

export const buildKioskCallbackItem = (task: KioskCallbackTask) => {
    const [title, callerName] = readContentParagraphs(task.content);
    const taskLinkPath = `/tasks/${task.id}/detail`;
    const callerLinkPath = buildCallerLinkPath(task);

    const assistantItem: AssistantItem = {
        id: `task-${task.id}-kiosk-callback`,
        type: "kiosk_callback",
        clientName: title || "🚨 Pedido de chamada",
        clientLinkPath: callerLinkPath ?? taskLinkPath,
        contentLinkPath: callerLinkPath,
        phone: extractKioskCallbackPhone(task.hidden_field_automations),
        content: callerName ?? "",
        linkPath: taskLinkPath,
        createdAt: task.date_created ?? new Date().toISOString(),
    };
    return assistantItem;
};
